import { useState } from "react";
import { submitLead } from "../lib/api";
import { dispatchLeadSubmitted } from "../lib/leadEvents";

/**
 * Reusable lead form.
 * Props:
 *   leadType: string (vehicle_inquiry, find_me_vehicle, ...)
 *   leadSource: string (page name)
 *   fields: list of field defs [{name,label,type,required,placeholder,options,rows}]
 *   hidden: object of extra hidden fields (vehicle context, etc.)
 *   submitLabel: button text
 *   onSuccess: callback (optional)
 *   compact: boolean (smaller layout)
 *   successMessage: override default thank-you text
 */
export default function LeadForm({
  leadType,
  leadSource,
  fields,
  hidden = {},
  submitLabel = "Send to Scott",
  onSuccess,
  compact = false,
  successMessage = "Thanks. I'll review this and follow up with you.",
  testIdPrefix = "lead",
}) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    for (const f of fields) {
      if (f.required && !String(form[f.name] || "").trim()) {
        return `Please fill in: ${f.label}`;
      }
    }
    // Require at least one way to reach back: phone OR email.
    // Phone wins — we do not force email if phone is provided.
    const phone = String(form.phone || "").trim();
    const email = String(form.email || "").trim();
    if (!phone && !email) {
      return "Please add a phone number or email so Scott can follow up.";
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return "Please enter a valid email address.";
    }
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const err = validate();
    if (err) { setError(err); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        ...hidden,
        lead_type: leadType,
        lead_source: leadSource,
        form_name: leadSource,
        page_url: typeof window !== "undefined" ? window.location.href : "",
        submitted_at: new Date().toISOString(),
      };
      const res = await submitLead(payload);
      if (res && res.ok) {
        setDone(true);
        dispatchLeadSubmitted({ leadType, leadSource });
        onSuccess && onSuccess(payload);
      } else {
        setError("Something went wrong. Please try again or text Scott directly.");
      }
    } catch (e) {
      setError("Something went wrong. Please try again or text Scott directly.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ssc-card text-center" data-testid={`${testIdPrefix}-success`}>
        <div className="text-2xl font-extrabold text-[var(--ssc-navy)]">Thanks!</div>
        <p className="mt-2 text-[var(--ssc-ink-2)]">{successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`ssc-card ${compact ? "p-4" : ""}`} data-testid={`${testIdPrefix}-form`}>
      {/* Honeypot */}
      <input type="text" name="website" className="ssc-hp" tabIndex="-1" autoComplete="off" onChange={set("website")} />
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "sm:grid-cols-2"}`}>
        {fields.map((f) => {
          const colSpan = f.full ? "sm:col-span-2" : "";
          if (f.type === "select") {
            return (
              <div key={f.name} className={colSpan}>
                <label className="ssc-label">{f.label}{f.required && " *"}</label>
                <select className="ssc-select" value={form[f.name] || ""} onChange={set(f.name)} data-testid={`${testIdPrefix}-${f.name}`}>
                  <option value="">{f.placeholder || "Select..."}</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            );
          }
          if (f.type === "textarea") {
            return (
              <div key={f.name} className="sm:col-span-2">
                <label className="ssc-label">{f.label}{f.required && " *"}</label>
                <textarea
                  className="ssc-textarea"
                  rows={f.rows || 4}
                  value={form[f.name] || ""}
                  onChange={set(f.name)}
                  placeholder={f.placeholder}
                  data-testid={`${testIdPrefix}-${f.name}`}
                />
              </div>
            );
          }
          return (
            <div key={f.name} className={colSpan}>
              <label className="ssc-label">{f.label}{f.required && " *"}</label>
              <input
                className="ssc-input"
                type={f.type || "text"}
                inputMode={f.inputMode}
                autoComplete={f.autoComplete}
                value={form[f.name] || ""}
                onChange={set(f.name)}
                placeholder={f.placeholder}
                data-testid={`${testIdPrefix}-${f.name}`}
              />
            </div>
          );
        })}
      </div>
      {error && (
        <div className="mt-4 text-sm text-[var(--ssc-red)]" data-testid={`${testIdPrefix}-error`}>{error}</div>
      )}
      <div className="mt-5 flex">
        <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full sm:w-auto" data-testid={`${testIdPrefix}-submit`}>
          {busy ? "Sending..." : submitLabel}
        </button>
      </div>
    </form>
  );
}
